import { useMemo, type ReactNode } from 'react';
import { NO_ROOFTOP_FACTS, composeResolvedProducts, isAdminViewerRole, resolveEffectiveDisplayPreferences, useOrderClockTick } from '@hopdrive/feature-order-index';
import type {
  OrderCardDisplayPreferences,
  OrderFeedEntry,
  OrderFeedResult,
  OrderFeedState,
  OrderFilterState,
  OrderIndexResolvedProduct,
  OrderProductKey,
  OrderRooftopFacts,
  OrderRowDisplayPreferences,
} from '@hopdrive/feature-order-index';

import { useUnreadMessages } from '@features/moves/hooks/useUnreadMessages';
import { resolveOrderPlugins } from '@features/orderIndex/plugins';
import { useOrderFeed } from '@features/orderIndex/shared/hooks/useOrderFeed';
import { useOrderWindow } from '@features/orderIndex/shared/hooks/useOrderWindow';
import { useMovesOrderOperationalModel } from '@features/orderIndex/useMovesOrderOperationalModel';
import { useCustomerConfig, useUser } from '@store';

const EMPTY_FILTERS: OrderFilterState = { search: '', market: [], createdBy: [], driver: [], provider: [], product: [], offerStatus: [] };

// Ownership filters the shared feed after querying all supported products.
export const HOME_PRODUCTS: OrderProductKey[] = ['moves', 'mobile-service', 'guest-rides', 'parts-delivery'];
const HOME_PLUGINS = resolveOrderPlugins(HOME_PRODUCTS);

// The one page the mixed feed hydrates; the zones re-slice and paginate within it. Beyond it the
// feed reports `truncated` so the page can say so rather than silently drop the latest orders.
const HOME_FEED_LIMIT = 1000;

export interface HomeOrderFeed {
  observers: ReactNode;
  feed: OrderFeedResult;
  resolved: OrderIndexResolvedProduct<unknown>;
  nowMs: number;
  preferences: { card: OrderCardDisplayPreferences; row: OrderRowDisplayPreferences };
  refetch: () => void;
  /** The window holds more orders than the feed hydrated, from the cross-product limit or a product's own cap. */
  truncated: boolean;
}

export interface UseHomeOrderFeedInput {
  rooftops: OrderRooftopFacts[];
  orderTypes: OrderProductKey[];
  workingRooftopId: number | undefined;
}

/**
 * Today's orders across the scope's rooftops and the customer's owned products, composed into one
 * resolver so a single grid renders every product's cards. The home zones slice it by phase.
 */
export const useHomeOrderFeed = ({ rooftops, orderTypes, workingRooftopId }: UseHomeOrderFeedInput): HomeOrderFeed => {
  const nowMs = useOrderClockTick();
  const isAdminViewer = isAdminViewerRole(useUser()?.profile?.default_role);
  const referenceNumLabel = (useCustomerConfig()?.reference_num_label as string | null | undefined) ?? null;

  // The working rooftop's facts drive per-product display formatting (timezone, reference label); a
  // multi-rooftop feed formats in the working rooftop's zone for now, as the adapter's Valet path does.
  // Its operational model comes from the product accessor (the scope-list facts carry a placeholder),
  // so Valet's null-fact orders fall back to the working customer's real model.
  const valetOperationalModel = useMovesOrderOperationalModel();
  const workingRooftop = useMemo(() => {
    const picked = rooftops.find(facts => facts.id === workingRooftopId) ?? rooftops[0] ?? NO_ROOFTOP_FACTS;
    return { ...picked, operationalModel: valetOperationalModel };
  }, [rooftops, workingRooftopId, valetOperationalModel]);

  const resolved = useMemo(
    () => composeResolvedProducts(HOME_PLUGINS.map(plugin => plugin.resolve({ rooftop: workingRooftop, isAdminViewer, referenceNumLabel }))),
    [workingRooftop, isAdminViewer, referenceNumLabel],
  );

  const window = useOrderWindow({ preset: 'today', startDate: '', endDate: '' });

  // Only Valet reads unread state.
  const { unreadMoves } = useUnreadMessages();
  const unreadMoveIds = useMemo(() => new Set(unreadMoves.map(unread => unread.moveId)), [unreadMoves]);
  const extras = useMemo(() => ({ moves: { unreadMoveIds } }), [unreadMoveIds]);

  const state = useMemo<OrderFeedState>(
    () => ({ metric: 'all', sortBy: resolved.defaultSort.sortBy, sortOrder: 'asc', groupBy: 'none', page: 0, pageSize: HOME_FEED_LIMIT, viewMode: 'cards' }),
    [resolved.defaultSort.sortBy],
  );

  const { feed, refetch, observers } = useOrderFeed({ plugins: HOME_PLUGINS, window, rooftops, includePayer: true, nowMs, extras, filters: EMPTY_FILTERS, state, resolved, isAdminViewer });

  const ownedFeed = useMemo<OrderFeedResult>(() => {
    const owned = new Set(orderTypes);
    const keep = (entry: OrderFeedEntry): boolean => entry.kind !== 'order' || owned.has(entry.model.product);
    const entries = feed.entries.filter(keep);
    return { ...feed, entries, pagedEntries: feed.pagedEntries.filter(keep), totalCount: entries.length };
  }, [feed, orderTypes]);

  const preferences = useMemo(
    () => ({
      card: resolveEffectiveDisplayPreferences({ preferences: {} as OrderCardDisplayPreferences, definitions: resolved.displayPreferences, isAdminViewer }),
      row: resolveEffectiveDisplayPreferences({ preferences: {} as OrderRowDisplayPreferences, definitions: resolved.displayPreferences, isAdminViewer }),
    }),
    [resolved.displayPreferences, isAdminViewer],
  );

  const truncated = feed.entries.length > feed.pagedEntries.length || feed.capReached;

  return { observers, feed: ownedFeed, resolved, nowMs, preferences, refetch, truncated };
};
